import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { MenuState } from '../reducer';
import { MenuActionKeys, openCustomItem } from '../actions';
import MenuItemRoute from './MenuItem/Route';
import MenuItemOnClick from './MenuItem/OnClick';
import Utils from '../../../utils';
import { MenuItemType, MenuItem } from '../../../typings/menu';
import { StyledLeftSideMenu, HeaderMenuItem } from './styles';
import { PageOverlay } from '../../../components/PageOverlay/styles';
import { disconnect } from '../../Authentication/actions';
import { AuthenticationState } from '../../Authentication/reducer';
import { AppInfosState, AppType } from '../../App/reducer';
import { GalleryInfosState } from '../../Gallery/reducer';
import UserAgentUtils from '../../../utils/userAgent';
import RoutesUtils from '../../../utils/routes';
import { DriveItemListOrigin } from '../../DriveItemListPage/constants';
import AuthUtils from '../../../utils/authentication';
import { MultiAppAuthenticationInfosState, AspConnexionStatus } from '../../MultiAppAuthentication/reducer';
import { signOut } from '../../MultiAppAuthentication/actions';

const Translate = require('react-redux-i18n').Translate;
const I18n = require('react-redux-i18n').I18n;

export interface LeftSideMenuProps {
	appInfos: AppInfosState;
	galleryInfos: GalleryInfosState;
	menuManager: MenuState;
	authentication: AuthenticationState;
	multiAppAuthenticationInfos: MultiAppAuthenticationInfosState;
	newItemsNb: number;
	dispatch: Dispatch<StoreState>;
}

export class LeftSideMenu extends React.Component<LeftSideMenuProps, {}> {

	constructor(props: LeftSideMenuProps) {
		super(props);
		this.handleLogoutClick = this.handleLogoutClick.bind(this);
		this.filterMenuItems = this.filterMenuItems.bind(this);
		this.getMenuItems = this.getMenuItems.bind(this);
	}

	renderMenuItemOnClick = (item: MenuItem, index: number) => {
		const { dispatch } = this.props;
		return (
			<MenuItemOnClick
				key={index}
				title={item.label}
				iconFileName={item.icon}
				badge={item.badge}
				onClick={() => dispatch(openCustomItem(item.type, item.target))}
			/>
		);
	}

	renderMenuItemRoute = (item: MenuItem, index: number) => {
		return (
			<MenuItemRoute
				key={index}
				route={item.target}
				title={I18n.t(item.label)}
				svgFileName={item.icon}
				badge={item.badge}
			/>
		);
	}

	render() {
		const { menuManager, authentication, appInfos, multiAppAuthenticationInfos } = this.props;
		const { mode } = authentication.userInfos;
		const { type: appType } = appInfos;
		const { aspConnexionStatus } = multiAppAuthenticationInfos;
		const { leftMenuIsOpen } = menuManager;

		const filteredMenuItems = this.filterMenuItems();
		const menuItems = this.getMenuItems(filteredMenuItems);

		return (
			<div>
				<PageOverlay isOpen={leftMenuIsOpen} onClick={this.handleOverlayClick} />
				<StyledLeftSideMenu isOpen={leftMenuIsOpen}>
					<HeaderMenuItem><Translate value="messages.menu" /></HeaderMenuItem>
					<MenuItemOnClick
						title={I18n.t('messages.back')}
						iconFileName="iconset_left"
						isBold={true}
						onClick={this.handleOverlayClick}
					/>
					{menuItems}
					{UserAgentUtils.isMobileApp &&
						(
							(appType === AppType.Single && !AuthUtils.isSaml(mode) && AuthUtils.isConnected(mode)) ||
							(appType === AppType.Multiple && aspConnexionStatus === AspConnexionStatus.SUCCESS )
						) &&
						<MenuItemOnClick
							title={I18n.t('messages.log_out')}
							iconFileName="iconset_logout"
							onClick={this.handleLogoutClick}
						/>
					}
				</StyledLeftSideMenu>
			</div>
		);
	}

	handleOverlayClick = () => {
		const { dispatch } = this.props;
		dispatch({ type: MenuActionKeys.REQUEST_CLOSE_LEFT_MENU });
	}

	handleLogoutClick = () => {
		const { dispatch, appInfos } = this.props;
		const { type: appType } = appInfos;

		if (appType === AppType.Multiple ) {
			dispatch(signOut());
		} else {
			dispatch(disconnect());
		}
	}

	filterMenuItems = () => {
		const { galleryInfos, menuManager, appInfos } = this.props;
		const { burger } = menuManager.menuModes;
		const appType = appInfos.type;
		const isGalleryButtonNeeded = galleryInfos.isConfigLoaded && galleryInfos.template.welcomepage
			&& (appType === AppType.Single ? galleryInfos.isLocalTemplateLoaded : galleryInfos.isRemoteTemplateLoaded);

		if (burger) {
			return burger.filter((item) => {
				switch (item.type) {
					case MenuItemType.Gallery:
						return isGalleryButtonNeeded;
					case MenuItemType.New:
						return UserAgentUtils.isOnline() || !Utils.isMaster(galleryInfos.config);
					case MenuItemType.Downloads:
					case MenuItemType.Updates:
					case MenuItemType.Parameters:
						return UserAgentUtils.isMobileApp;
					default:
						return item;
				}
			});
		}
		return [];
	}

	getMenuItems = (items: MenuItem[]) => {
		return items.map((item, index) => {
			let itemArgs;

			switch (item.type) {
				case MenuItemType.Home:
					itemArgs = {
						type: MenuItemType.Home,
						label: 'messages.home',
						icon: 'iconset_home',
						target: RoutesUtils.getPathHome()
					};
					return this.renderMenuItemRoute(itemArgs, index);
				case MenuItemType.Gallery:
					itemArgs = {
						type: MenuItemType.Gallery,
						label: 'messages.my_gallery',
						icon: 'iconset_image',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, null)
					};
					return this.renderMenuItemRoute(itemArgs, index);
				case MenuItemType.New:
					itemArgs = {
						type: MenuItemType.New,
						label: 'messages.new',
						icon: 'iconset_news',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.New, null),
						badge: this.props.newItemsNb
					};
					return this.renderMenuItemRoute(itemArgs, index);
				case MenuItemType.Favorites:
					itemArgs = {
						type: MenuItemType.Favorites,
						label: 'messages.favorites',
						icon: 'iconset_favorite',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Favorites, null)
					};
					return this.renderMenuItemRoute(itemArgs, index);
				case MenuItemType.Downloads:
					itemArgs = {
						type: MenuItemType.Downloads,
						label: 'messages.my_downloads',
						icon: 'iconset_download',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Downloads, null)
					};
					return this.renderMenuItemRoute(itemArgs, index);
				case MenuItemType.Updates:
					itemArgs = {
						type: MenuItemType.Updates,
						label: 'messages.updates',
						icon: 'iconset_update',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Updates, null)
					};
					return this.renderMenuItemRoute(itemArgs, index);
				case MenuItemType.Parameters:
					itemArgs = {
						type: MenuItemType.Parameters,
						label: 'messages.parameters',
						icon: 'iconset_parameters',
						target: RoutesUtils.getPathParameters()
					};
					return this.renderMenuItemRoute(itemArgs, index);
				case MenuItemType.Link:
					itemArgs = {
						type: MenuItemType.Link,
						label: item.label ? item.label : 'Link',
						icon: item.icon ? item.icon : 'iconset_link',
						target: item.target
					};
					return this.renderMenuItemOnClick(itemArgs, index);
				case MenuItemType.Asset:
					itemArgs = {
						type: MenuItemType.Asset,
						label: item.label ? item.label : 'Asset',
						icon: item.icon ? item.icon : 'iconset_file',
						target: item.target
					};
					return this.renderMenuItemOnClick(itemArgs, index);
				case MenuItemType.Folder:
					itemArgs = {
						type: MenuItemType.Folder,
						label: item.label ? item.label : 'Folder',
						icon: item.icon ? item.icon : 'iconset_folder',
						target: item.target
					};
					return this.renderMenuItemOnClick(itemArgs, index);
				case MenuItemType.Page:
					itemArgs = {
						type: MenuItemType.Page,
						label: item.label ? item.label : 'Page',
						icon: item.icon ? item.icon : 'iconset_page',
						target: item.target,
					};
					return this.renderMenuItemOnClick(itemArgs, index);
			}
			return null;
		});
	}
}

const mapStateToProps = (state: StoreState) => {
	const { menuManager, authentication, appInfos, galleryInfos, multiAppAuthenticationInfos, newDriveItems } = state;
	const newItemsNb = newDriveItems.driveItemIds.length;
	return { menuManager, authentication, appInfos, galleryInfos, multiAppAuthenticationInfos, newItemsNb };
};

export default connect(mapStateToProps)(LeftSideMenu);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/MenuManager/Left/index.tsx